import { FormattedMessage } from "react-intl";
import { ErrorBoundary } from "react-error-boundary";

// Hooks
import useSetupCompany from "./useSetupCompany";
import useResizeObserver from "use-resize-observer";

// Material UI Components
import { Box, FormLabel, Grid, MenuItem, Modal, Select, Typography } from "@mui/material";

// Components
import CustomIcon from "../CustomIcon";
import TeamLayout from "components/Team";
import { Header } from "components/Layout/LayoutContent";
import { CompanySummary } from "components/CompanySummary";
import GenericError from "components/Errors/GenericError";
import HeaderNavigation from "components/HeaderNavigation/HeaderNavigation";
import useSetTeamNavActions from "components/HeaderNavigation/NavHooks/useSetTeamNavActions";
import AutocompleteField, { useAutocompleteSearchAIs } from "components/Autocomplete/AutocompleteField";

// Icons
import icons from "enums/icons";

// Styles
import setupCompanyStyles from "./SetupCompany.styles";

// Types
import { TSearchAIs } from "components/Autocomplete/AutocompleteField/hooks/useAutocompleteSearchAIs";
import { ChartErrorMessage } from "components/ChartErrorMessage";

// Consider removing this component and merge it inside TeamLayout.tsx
const SetupCompany = (): JSX.Element => {
    const { ref, width = -1, height = -1 } = useResizeObserver<HTMLDivElement>();

    const {
        shouldShow,
        shouldNotFetchBoard,
        hasRelatedLongList,
        currentSearchAI,
        searchAiOptions,
        hasBoardMembers,
        companiesOptions,
        selectedCompanyOption,
        handleChangeCompany,
        handleSelectSearchAi,
    } = useSetupCompany();

    const classes = setupCompanyStyles(hasRelatedLongList);

    const { backButton, nextButton } = useSetTeamNavActions(currentSearchAI);
    const { placeholderSearchAI, getOptionLabelSearchAI, renderOptionSearchAI } = useAutocompleteSearchAIs();

    const RefinementAutocomplete = () => (
        <Box width="75%" sx={{ margin: "auto" }}>
            <Box display="flex" alignItems="center" mb={0.5}>
                <CustomIcon icon={icons.flashLight} sx={{ mr: 1 }} />
                <Typography component="strong" sx={{ fontSize: 17 }}>
                    <FormattedMessage id="setup-company.teams.search.ai.label" />
                </Typography>
            </Box>
            <AutocompleteField
                showValue
                blurOnSelect
                inputSize="small"
                showStartAdorment
                showEndAdorment={false}
                options={searchAiOptions}
                filterOptions={(x) => x}
                id="search-related-search-ai"
                placeholder={placeholderSearchAI}
                renderOption={renderOptionSearchAI}
                getOptionLabel={getOptionLabelSearchAI}
                value={searchAiOptions.filter((e) => e.id === currentSearchAI)[0]}
                selectedOptions={searchAiOptions
                    .filter((e) => e.id === currentSearchAI)
                    .map((e) => ({
                        key: e.id,
                        value: e.name,
                        selected: true,
                    }))}
                isOptionEqualToValue={(option: TSearchAIs, value: TSearchAIs) => option.id === value.id}
                onOptionSelectCallback={handleSelectSearchAi}
            />
        </Box>
    );

    const PeerSelector = () => {
        return companiesOptions.length ? (
            <>
                <FormLabel className={classes.peerLabel}>Set Board Structures</FormLabel>
                <Select
                    id="companies"
                    name="companies"
                    value={selectedCompanyOption?.toString()}
                    className={classes.peerSelect}
                    onChange={handleChangeCompany}
                    IconComponent={(props) => (
                        <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                    )}
                >
                    {companiesOptions.map((peer) => {
                        return (
                            <MenuItem key={peer.id} value={peer.id}>
                                {peer.name}
                            </MenuItem>
                        );
                    })}
                </Select>
            </>
        ) : (
            <></>
        );
    };

    return (
        <>
            <ErrorBoundary FallbackComponent={GenericError}>
                <Header title={<HeaderNavigation title="Set Team" backButton={backButton} nextButton={nextButton} />}>
                    <CompanySummary
                        middlePlaceholder={
                            <Box width="75%" sx={{ margin: "auto" }}>
                                {Boolean(hasRelatedLongList && searchAiOptions.length) && <RefinementAutocomplete />}
                                {/* LD-3019 - Temporary hidden */}
                                {/* {Boolean(!hasRelatedLongList) && <PeerSelector />} */}
                            </Box>
                        }
                    />
                </Header>
                {shouldShow && (
                    <Grid container className={classes.refineDomainWrapper} ref={ref}>
                        <TeamLayout
                            width={width}
                            height={height}
                            showLongList={hasRelatedLongList}
                            editTeam={shouldNotFetchBoard}
                            selectedSearchAI={`${currentSearchAI}`}
                        />
                    </Grid>
                )}
                <Modal open={!hasBoardMembers}>
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            justifyContent: "center",
                            alignItems: "center",
                            display: "flex",
                            position: "fixed",
                            backgroundColor: "rgb(255 255 255 / 80%)",
                            top: 0,
                            zIndex: 3,
                        }}
                    >
                        <ChartErrorMessage />
                    </Box>
                </Modal>
            </ErrorBoundary>
        </>
    );
};

export default SetupCompany;
